import AddIcon from "@mui/icons-material/Add";
import {
    Box,
    Fab
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    createNotificationMessage,
    deleteNotificationMessage,
    getNotificationCategories,
    getNotificationMessages,
    updateNotificationMessage,
} from "../../../../actions/notificaciones";
import snackbarUtils from "../../../../utils/snackbarUtils";
import NotificacionPlantillasDialog from "./notificacionPlantillasDialog";
import NotificacionPlantillasFilters from "./notificacionPlantillasFilters";
import NotificacionPlantillasTable from "./notificacionPlantillasTable";

const EMPTY_FORM = {
    nombre: "",
    categoria: "",
    descripcion: "",
};

const NotificacionPlantillasContainer = () => {
    const dispatch = useDispatch();
    const { list, categories, isFetching, isSaving } = useSelector((state) => state.notificaciones);

    const [filters, setFilters] = useState({ categoria: "" });
    const [openDialog, setOpenDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);

    const canSave = useMemo(() => {
        return Boolean(form.nombre.trim() && form.categoria.trim() && form.descripcion.trim());
    }, [form]);

    useEffect(() => {
        dispatch(getNotificationCategories());
        dispatch(getNotificationMessages());
    }, []);

    const refreshWithFilters = () => {
        dispatch(getNotificationMessages(filters.categoria || undefined));
    };

    const handleOpenNew = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setOpenDialog(true);
    };

    const handleOpenEdit = (item) => {
        setEditing(item);
        setForm({
            nombre: item.nombre || "",
            categoria: item.categoria || "",
            descripcion: item.descripcion || "",
        });
        setOpenDialog(true);
    };

    const handleSave = () => {
        if (isSaving) {
            return;
        }

        const payload = {
            nombre: form.nombre,
            categoria: form.categoria,
            descripcion: form.descripcion,
        };

        const action = editing
            ? updateNotificationMessage(editing.id, payload)
            : createNotificationMessage(payload);

        dispatch(action)
            .then(() => {
                snackbarUtils.success(editing ? "Plantilla actualizada." : "Plantilla creada.");
                setOpenDialog(false);
                refreshWithFilters();
                dispatch(getNotificationCategories());
            })
            .catch(() => {
                snackbarUtils.error("No se pudo guardar la plantilla.");
            });
    };

    const handleDelete = (id) => {
        if (!window.confirm("¿Confirmas eliminar esta plantilla?")) {
            return;
        }

        dispatch(deleteNotificationMessage(id))
            .then(() => {
                snackbarUtils.success("Plantilla eliminada.");
                refreshWithFilters();
                dispatch(getNotificationCategories());
            })
            .catch(() => {
                snackbarUtils.error("No se pudo eliminar la plantilla.");
            });
    };

    return (
        <Box p={3}>

            <NotificacionPlantillasFilters
                categories={categories}
                categoria={filters.categoria}
                onCategoriaChange={(categoria) => setFilters((prev) => ({ ...prev, categoria }))}
                onSearch={refreshWithFilters}
            />

            <NotificacionPlantillasTable
                data={list}
                loading={isFetching}
                onEdit={handleOpenEdit}
                onDelete={handleDelete}
                fetchData={refreshWithFilters}
            />

            <NotificacionPlantillasDialog
                open={openDialog}
                editing={editing}
                form={form}
                categories={categories}
                canSave={canSave}
                isSaving={isSaving}
                onClose={() => setOpenDialog(false)}
                onSave={handleSave}
                onNombreChange={(value) => setForm((prev) => ({ ...prev, nombre: value }))}
                onCategoriaChange={(value) => setForm((prev) => ({ ...prev, categoria: value }))}
                onDescripcionChange={(value) => setForm((prev) => ({ ...prev, descripcion: value }))}
            />

            <Fab
                color="primary"
                aria-label="Nueva plantilla"
                onClick={handleOpenNew}
                sx={{
                    position: "fixed",
                    bottom: 24,
                    right: 24,
                    zIndex: 1300,
                }}>
                <AddIcon />
            </Fab>
        </Box>
    );
};

export default NotificacionPlantillasContainer;
